import { type CustomAdventure } from '@/utils/businessAppApi';

/**
 * Utility functions for Custom Adventures
 */

// Currency formatting utility
export const formatCurrency = (amount: number, currencyCode: string): string => {
  const currencySymbol = currencyCode === 'EUR' ? '€' : 
                         currencyCode === 'GBP' ? '£' : '$';
  
  // Defensive programming: handle undefined/null amounts
  const safeAmount = amount || 0;
  return `${currencySymbol}${safeAmount.toFixed(2)}`;
};

// Adventure emoji generation utility
export const getAdventureEmoji = (adventure: CustomAdventure): string => {
  if (!adventure || !adventure.options || !Array.isArray(adventure.options)) {
    return '🏔️'; // Default emoji for adventures
  }
  
  const optionTypes = adventure.options.map(opt => opt.type.toLowerCase());
  
  if (optionTypes.some(type => type.includes('animal') || type.includes('wildlife'))) {
    return '🦁';
  } else if (optionTypes.some(type => type.includes('location') || type.includes('destination'))) {
    return '🏞️';
  } else if (optionTypes.some(type => type.includes('time') || type.includes('duration'))) {
    return '⏱️';
  } else if (optionTypes.some(type => type.includes('transport') || type.includes('vehicle'))) {
    return '🚙';
  } else if (optionTypes.some(type => type.includes('food') || type.includes('meal'))) {
    return '🍽️';
  } else if (optionTypes.some(type => type.includes('gear') || type.includes('equipment'))) {
    return '🎒';
  }
  
  return '✨';
};

// Adventure duration utility
export const getAdventureDuration = (adventure: CustomAdventure): string => {
  const durationOption = adventure.options.find(opt => 
    opt.label.toLowerCase().includes('time') || 
    opt.label.toLowerCase().includes('duration') ||
    opt.label.toLowerCase().includes('hour') ||
    opt.type.toLowerCase().includes('duration')
  );
  
  if (durationOption && durationOption.options && durationOption.options.length > 0) {
    const firstOption = durationOption.options[0];
    return typeof firstOption === 'string' ? firstOption : firstOption.value;
  }
  
  return "Variable duration";
};

// Adventure characteristics generator
export const getAdventureCharacteristics = (adventure: CustomAdventure) => {
  const characteristics = [];
  
  // Check for group size
  const groupOption = adventure.options.find(opt => 
    opt.label.toLowerCase().includes('people') || 
    opt.label.toLowerCase().includes('group') ||
    opt.label.toLowerCase().includes('adults')
  );
  if (groupOption && groupOption.options) {
    const maxSize = groupOption.options.length > 0 ? 
      Math.max(...groupOption.options.map(o => parseInt(typeof o === 'string' ? o : o.value) || 1)) : null;
    if (maxSize) {
      characteristics.push({ label: `Up to ${maxSize}`, icon: '👥', type: 'capacity' });
    }
  }
  
  // Check for duration
  const duration = getAdventureDuration(adventure);
  if (duration !== "Variable duration") {
    characteristics.push({ label: duration, icon: '⏱️', type: 'duration' });
  }
  
  // Check for special features
  const features = adventure.options.filter(opt => 
    opt.type.toLowerCase().includes('feature') || 
    opt.type.toLowerCase().includes('equipment') ||
    opt.type.toLowerCase().includes('meal') ||
    opt.type.toLowerCase().includes('transport')
  ).slice(0, 2); // Limit to 2 features
  
  features.forEach(feature => {
    const icon = feature.type.toLowerCase().includes('meal') ? '🍽️' :
                 feature.type.toLowerCase().includes('transport') ? '🚗' :
                 feature.type.toLowerCase().includes('equipment') ? '🎒' : '✨';
    characteristics.push({ 
      label: feature.label.length > 12 ? feature.label.substring(0, 12) + '...' : feature.label, 
      icon, 
      type: 'feature' 
    });
  });
  
  return characteristics.slice(0, 3); // Limit to 3 total characteristics
};

// Adventure gradient color generator
export const generateAdventureGradient = (str: string) => {
  const hash = str.split('').reduce((hash, char) => {
    return char.charCodeAt(0) + ((hash << 5) - hash);
  }, 0);
  
  // Professional color palettes
  const palettes = [
    { from: 'from-emerald-400', to: 'to-teal-500', bg: 'bg-emerald-50', text: 'text-emerald-600' },
    { from: 'from-blue-400', to: 'to-indigo-500', bg: 'bg-blue-50', text: 'text-blue-600' },
    { from: 'from-purple-400', to: 'to-pink-500', bg: 'bg-purple-50', text: 'text-purple-600' },
    { from: 'from-orange-400', to: 'to-red-500', bg: 'bg-orange-50', text: 'text-orange-600' },
    { from: 'from-green-400', to: 'to-emerald-500', bg: 'bg-green-50', text: 'text-green-600' },
    { from: 'from-cyan-400', to: 'to-blue-500', bg: 'bg-cyan-50', text: 'text-cyan-600' }
  ];
  
  return palettes[Math.abs(hash) % palettes.length];
};
